import { ListBulletIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { cn } from "@/core/utils/utils";
import type { ComparisonSheetSummary } from "@/payroll/editor/types";

/**
 * Every sheet in one list, the way the payroll grid offers them.
 *
 * The strip beside it holds the sheets you are moving between; this holds all
 * of them. A real workbook here runs to 376, so the strip is a window onto a
 * list far longer than it, and scrolling sideways through hundreds of names to
 * reach one you can already name is not a way to find anything.
 *
 * It leads the strip rather than following it, so the control that opens the
 * whole list sits where the list starts.
 */
export const SheetSwitcher = ({
  sheets,
  activeIndex,
  onSelect,
}: {
  sheets: ComparisonSheetSummary[];
  activeIndex: number | null;
  onSelect: (index: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="All sheets"
          title="All sheets"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-secondary-text transition-colors hover:bg-surface-secondary hover:text-primary-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <ListBulletIcon className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" className="w-80 p-0">
        <Command>
          <CommandInput placeholder="Find a sheet..." />
          <CommandList className="max-h-72 overflow-y-auto">
            <CommandEmpty>No sheets found.</CommandEmpty>
            <CommandGroup heading="Sheets">
              {sheets.map((sheet) => (
                <CommandItem
                  key={sheet.index}
                  // The index goes in the value so two sheets that share a name
                  // are still two rows the filter can tell apart.
                  value={`${sheet.name} ${sheet.index}`}
                  onSelect={() => {
                    onSelect(sheet.index);
                    setOpen(false);
                  }}
                  className="min-w-0 gap-2"
                >
                  <span className="min-w-0 flex-1 truncate">{sheet.name}</span>
                  {/* The same mark the strip carries, so a sheet that changed
                      looks the same in both places. */}
                  {sheet.state !== "unchanged" && (
                    <span
                      aria-label={`${sheet.changed_count} changed`}
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-stroke-interactive"
                    />
                  )}
                  <CheckIcon
                    aria-hidden
                    className={cn("h-4 w-4 shrink-0", {
                      "opacity-100": sheet.index === activeIndex,
                      "opacity-0": sheet.index !== activeIndex,
                    })}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
